import { useEffect, useRef, useState } from "react";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Carousel, CarouselContent, CarouselItem } from "@/components/ui/carousel";
import TiltCard from "@/components/TiltCard";
import { api as httpApi } from "@/lib/api";

const AUTOPLAY_INTERVAL_MS = 5500;
const MANUAL_PAUSE_MS = 9000;

export default function FeaturedAthletesCarousel() {
  const [athletes, setAthletes] = useState([]);
  const [api, setApi] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const nextAdvanceAt = useRef(Date.now() + AUTOPLAY_INTERVAL_MS);

  useEffect(() => {
    httpApi.get("/public/hall-of-fame/carousel").then(({ data }) => setAthletes(data.map((group) => {
      const achievements = group.achievements || [];
      const first = achievements[0] || {};
      return { name: group.athlete_name, initials: group.athlete_name.trim().split(/\s+/).slice(0, 2).map((part) => part[0]).join("").toUpperCase(), medal: first.medal || "Destaque", highlight: first.title || "", achievements: achievements.map((item) => [item.title, item.competition, item.year].filter(Boolean).join(" — ")), background: "from-amber-500/15 to-[#102944]" };
    }))).catch(() => setAthletes([]));
  }, []);

  useEffect(() => {
    const refresh = () => {
      if (document.hidden) return;
      httpApi.get("/public/hall-of-fame/carousel").then(({ data }) => {
        if (!Array.isArray(data)) return;
        setAthletes(data.map((group) => {
          const achievements = group.achievements || [];
          const first = achievements[0] || {};
          return { name: group.athlete_name, initials: group.athlete_name.trim().split(/\s+/).slice(0, 2).map((part) => part[0]).join("").toUpperCase(), medal: first.medal || "Destaque", highlight: first.title || "", achievements: achievements.map((item) => [item.title, item.competition, item.year].filter(Boolean).join(" — ")), background: "from-amber-500/15 to-[#102944]" };
        }));
      }).catch(() => {});
    };
    const timer = window.setInterval(refresh, 30000);
    window.addEventListener("focus", refresh);
    return () => { window.clearInterval(timer); window.removeEventListener("focus", refresh); };
  }, []);

  useEffect(() => {
    if (!api) return;
    const updateIndex = () => setActiveIndex(api.selectedScrollSnap());
    updateIndex();
    api.on("select", updateIndex);
    api.on("reInit", updateIndex);
    return () => {
      api.off("select", updateIndex);
      api.off("reInit", updateIndex);
    };
  }, [api]);

  useEffect(() => {
    if (!api) return;
    nextAdvanceAt.current = Date.now() + AUTOPLAY_INTERVAL_MS;
    const timer = window.setInterval(() => {
      if (document.hidden || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
      if (Date.now() >= nextAdvanceAt.current) {
        api.scrollNext();
        nextAdvanceAt.current = Date.now() + AUTOPLAY_INTERVAL_MS;
      }
    }, 500);
    return () => window.clearInterval(timer);
  }, [api]);

  const pauseAfterManualChange = () => {
    nextAdvanceAt.current = Date.now() + MANUAL_PAUSE_MS;
  };

  const move = (direction) => {
    pauseAfterManualChange();
    if (direction === "previous") api?.scrollPrev();
    else api?.scrollNext();
  };

  return (
    <div
      className="min-w-0"
      data-testid="featured-athletes-carousel"
      onKeyDownCapture={(event) => {
        if (event.key === "ArrowLeft" || event.key === "ArrowRight") pauseAfterManualChange();
      }}
    >
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-widest text-amber-400">Hall da Fama GSX</p>
          <h3 className="mt-1 text-xl font-semibold text-white sm:text-2xl">Títulos de <span className="text-amber-400">Destaque Nacional</span></h3>
          <p className="mt-2 text-xs text-slate-300">Atletas do Grupo Santo de Xadrez que levaram Sergipe ao pódio nacional.</p>
        </div>
        <Link to="/hall-da-fama" className="mt-5 inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-amber-300 transition-colors hover:text-amber-200" data-testid="featured-hall-see-more">
          Veja mais <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
        </Link>
      </div>

      {athletes.length ? <><Carousel opts={{ loop: athletes.length > 1 }} setApi={setApi} aria-label="Atletas em destaque">
        <CarouselContent>
          {athletes.map((athlete) => (
            <CarouselItem key={athlete.name}>
              <div className="p-4 sm:p-5">
                <TiltCard max={5} className={`flex min-h-[290px] flex-col rounded-2xl border border-amber-400/15 bg-gradient-to-br ${athlete.background} p-6 sm:p-8`}>
                  <div className="flex items-center justify-between gap-3">
                    <span className="flex h-14 w-14 items-center justify-center rounded-full border border-amber-400/50 bg-amber-400/15 font-mono text-xl font-semibold text-amber-400" aria-hidden="true">{athlete.initials}</span>
                    <span className="text-right text-[10px] font-bold uppercase tracking-wider text-amber-300">{athlete.medal}</span>
                  </div>
                  <h4 className="mt-6 text-xl font-semibold text-white">{athlete.name}</h4>
                  <p className="mt-1 text-sm font-medium text-amber-300">{athlete.highlight}</p>
                  <ul className="mt-5 space-y-2 text-sm text-slate-200">
                    {athlete.achievements.map((achievement) => (
                      <li key={achievement} className="flex gap-2"><span className="text-amber-400" aria-hidden="true">•</span>{achievement}</li>
                    ))}
                  </ul>
                </TiltCard>
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>

      <div className="mt-4 flex items-center justify-center gap-4">
        <Button type="button" variant="outline" size="icon" onClick={() => move("previous")} disabled={athletes.length < 2} aria-label="Atleta anterior" data-testid="featured-athlete-prev" className="h-9 w-9 rounded-full border-amber-400/30 bg-[#0a203c] text-amber-300 hover:bg-amber-400/10 hover:text-amber-200"><ChevronLeft className="h-4 w-4" /></Button>
        <span className="min-w-10 text-center text-xs font-medium text-slate-300" aria-live="off">{activeIndex + 1} / {athletes.length}</span>
        <Button type="button" variant="outline" size="icon" onClick={() => move("next")} aria-label="Próximo atleta" data-testid="featured-athlete-next" className="h-9 w-9 rounded-full border-amber-400/30 bg-[#0a203c] text-amber-300 hover:bg-amber-400/10 hover:text-amber-200"><ChevronRight className="h-4 w-4" /></Button>
      </div>
      </> : <p role="status" className="rounded-xl border border-white/10 p-8 text-center text-slate-400">Nenhum atleta foi selecionado para o carrossel.</p>}
    </div>
  );
}
